export interface NativeVideoRecoveryLadderOptions {
  baseMs?: number;
  maxMs?: number;
  maxAttempts?: number;
  windowMs?: number;
  healthyResetMs?: number;
}

/** Terminal Electron leave reasons only. IPC rejects stay on status.reason (see NativeVideoReject). */
export type WebCodecsFallbackReason = 'guest-unsupported' | 'settings-changed' | 'codec-unsupported';

const FALLBACK_REASONS: readonly WebCodecsFallbackReason[] = ['guest-unsupported', 'settings-changed', 'codec-unsupported'];

/** Maps a signaled reason onto the only fallbacks that may leave WebCodecs. */
export function webCodecsFallbackReason(reason: string | undefined): WebCodecsFallbackReason | undefined {
  if (reason === 'unsupported' || reason === 'NotSupportedError') return 'codec-unsupported';
  return FALLBACK_REASONS.find((allowed) => allowed === reason);
}

/**
 * Restart only after a stall has had no bytes for 3s and a keyframe request
 * has gone unanswered for 1s. `hiddenMs` is time the stall clock must ignore.
 */
export function webCodecsStallRestartReady(nowMs: number, stallSinceMs: number, keyframeAtMs: number, hiddenMs = 0): boolean {
  return nowMs - stallSinceMs - hiddenMs >= 3000 && nowMs - keyframeAtMs - hiddenMs >= 1000;
}

/**
 * In-path WebCodecs restart spacing. The delay grows from 250ms to 2s and then
 * stays there: hitting the attempt cap never reports exhaustion.
 */
export class NativeVideoRecoveryLadder {
  private readonly baseMs: number;
  private readonly maxMs: number;
  private readonly maxAttempts: number;
  private readonly windowMs: number;
  private readonly healthyResetMs: number;
  private attempts: number[] = [];
  private healthySinceMs: number | null = null;

  constructor(options: NativeVideoRecoveryLadderOptions = {}) {
    this.baseMs = options.baseMs ?? 250;
    this.maxMs = options.maxMs ?? 2000;
    this.maxAttempts = options.maxAttempts ?? 8;
    this.windowMs = options.windowMs ?? 60_000;
    this.healthyResetMs = options.healthyResetMs ?? 10_000;
  }

  /** Next restart delay. Null is unused: a full window still restarts at `maxMs`. */
  nextDelayMs(nowMs: number): number | null {
    this.healthySinceMs = null;
    this.attempts = this.attempts.filter((at) => nowMs - at < this.windowMs);
    const index = this.attempts.length;
    this.attempts.push(nowMs);
    if (index >= this.maxAttempts) return this.maxMs;
    return Math.min(this.maxMs, this.baseMs * 2 ** index);
  }

  /** Clears backoff once frames have been healthy for the reset interval. */
  recordHealthy(nowMs: number): void {
    if (this.healthySinceMs === null) this.healthySinceMs = nowMs;
    if (nowMs - this.healthySinceMs >= this.healthyResetMs) {
      this.attempts = [];
      this.healthySinceMs = null;
    }
  }
}
